import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { CloudSun, Sparkles, Lock, ArrowUpRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

function currentSeason(lang) {
  const m = new Date().getMonth() + 1; // 1..12
  if (m === 12 || m <= 2) return "winter";
  if (m <= 5) return "spring";
  if (m <= 8) return "summer";
  return "autumn";
}

export default function WeatherWidget({ liveWeatherEnabled }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const season = useMemo(currentSeason, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/municipalities/upgrade-request", {
        module: "live_weather",
        note: note.trim() || undefined,
      });
      toast.success(t("upgrade_sent"));
      setOpen(false);
      setNote("");
    } catch (err) {
      const code = err.response?.status;
      toast.error(
        code === 429
          ? t("upgrade_rate_limited")
          : formatApiErrorDetail(err.response?.data?.detail) || t("upgrade_error"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (liveWeatherEnabled) {
    return (
      <Card className="border-primary/40 bg-primary/[0.03]" data-testid="weather-widget-active">
        <CardContent className="p-5">
          <div className="flex items-center gap-2">
            <CloudSun className="h-4 w-4 text-primary" />
            <div className="label-caps text-primary">{t("weather_module_title")}</div>
          </div>
          <div className="mt-2 text-sm text-primary">{t("weather_module_active")}</div>
          <div className="mt-1 text-xs text-muted-foreground">{t("weather_module_benefit")}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-border" data-testid="weather-widget-inactive">
        <CardContent className="grid gap-4 p-5 md:grid-cols-5 md:p-6">
          <div className="md:col-span-3">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <div className="label-caps">{t("weather_module_title")}</div>
            </div>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="weather-inactive-hint">
              {t("weather_module_inactive")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{t("weather_module_benefit")}</p>

            <div className="mt-4 rounded-md border border-border bg-muted/40 p-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <div className="label-caps text-primary">{t("seasonal_tips_title")}</div>
              </div>
              <div className="mt-1 text-xs font-semibold">
                {t(`season_${season}`)}
              </div>
              <p className="mt-1 text-sm leading-relaxed" data-testid="seasonal-tip-text">
                {t(`season_tip_${season}`)}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">{t("weather_alternative")}</p>
            </div>
          </div>

          <div className="flex flex-col justify-center gap-3 md:col-span-2">
            <div className="rounded-lg border border-primary/40 bg-primary/[0.06] p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                Premium
              </div>
              <div className="mt-1 text-sm font-semibold">{t("weather_module_title")}</div>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("weather_module_benefit")}
              </p>
              <Button
                className="mt-3 w-full gap-2"
                onClick={() => setOpen(true)}
                data-testid="upgrade-weather-btn"
              >
                <ArrowUpRight className="h-4 w-4" />
                {t("upgrade_activate_weather")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="upgrade-weather-dialog">
          <DialogHeader>
            <DialogTitle>{t("upgrade_dialog_title")}</DialogTitle>
            <DialogDescription>{t("upgrade_dialog_body")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm">{t("upgrade_note_label")}</label>
            <Textarea
              rows={4}
              placeholder={t("upgrade_note_placeholder")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="upgrade-note-input"
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              {t("cancel")}
            </Button>
            <Button onClick={submit} disabled={busy} data-testid="upgrade-submit-btn">
              {busy ? "…" : t("upgrade_send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
